import { z } from 'zod'

// =====================
// Ticket CSAT (satisfaction surveys)
// =====================
//
// One survey row per ticket, created when a ticket of a survey-enabled type
// (TicketTypeDef.sendCsatSurvey) first reaches a RESOLVED/CLOSED stage. The
// requester rates via a public token link — one-click buttons in the
// resolution email or the client portal. The rating is denormalized onto
// tickets.csatRating for list columns, ScQL, and analytics.

// 1 = bad, 2 = okay, 3 = good.
export const CSAT_RATINGS = [
  { value: 1, key: 'bad', emoji: '\u{1F61E}', label: 'Bad' },
  { value: 2, key: 'okay', emoji: '\u{1F610}', label: 'Okay' },
  { value: 3, key: 'good', emoji: '\u{1F60A}', label: 'Good' },
] as const

export type CsatRatingValue = (typeof CSAT_RATINGS)[number]['value']

export const TicketSatisfactionSurveySchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  ticketId: z.string().uuid(),
  token: z.string(),
  rating: z.number().int().min(1).max(3).nullable().optional(),
  comment: z.string().nullable().optional(),
  recipientEmail: z.string().nullable().optional(),
  recipientName: z.string().nullable().optional(),
  // EMAIL | PORTAL — which surface captured the response.
  respondedVia: z.string().nullable().optional(),
  sentAt: z.coerce.date().nullable().optional(),
  respondedAt: z.coerce.date().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

export type TicketSatisfactionSurvey = z.infer<typeof TicketSatisfactionSurveySchema>

// Public submit body (token comes from the URL). Ratings can be revised
// (e.g. clicked the wrong emoji in the email) until the survey locks.
export const SubmitCsatResponseSchema = z.object({
  rating: z.number().int().min(1).max(3),
  comment: z.string().max(4000).optional(),
})

export type SubmitCsatResponse = z.infer<typeof SubmitCsatResponseSchema>

// How long after first response a survey stays editable (rating revisions
// and comment additions via the same token).
export const CSAT_RESPONSE_EDIT_WINDOW_DAYS = 7
